import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { COMPANIES, CONTACTS, MARKETING_CAMPAIGN_ACTIVITY } from 'constants/Entities';
import { getLiteral, getLiteralPluralized, getLiteralWithParameters } from 'utils/getLiteral';

export const MARKETING_CAMPAIGN_EXPORT_SCHEMA_ID = MARKETING_CAMPAIGN_ACTIVITY.trueName;

export const MARKETING_CAMPAIGN_ORIGIN_LABEL = {
    SENT_BY: 'label_sent_by',
    SCHEDULED_BY: 'label_scheduled_by',
};

const PARENT_ENTITY_FILTER_KEYS = new Set([COMPANIES.entity, CONTACTS.entity]);

const toMetricValue = (value) => (value === undefined || value === null ? 0 : value);

/**
 * Builds the Companies/Contacts filter for MC activity detail when opened from an entity widget.
 * BE needs this scope to return Campaign.IndividualMetrics for the parent recipient.
 */
export const buildMarketingCampaignDetailParentFilter = (entityType, entityId) => {
    const entityKey = entityType?.entity;
    if (!entityKey || !entityId || !PARENT_ENTITY_FILTER_KEYS.has(entityKey)) {
        return null;
    }

    return {
        [entityKey]: {
            dataType: 'singleValueList',
            id: entityKey,
            value: [{ Id: entityId }],
        },
    };
};

/**
 * Resolves the display name/id for the campaign activity executor title.
 * Immediate send → activity owner; scheduled → last modifier (audit.modified.user).
 */
export const getMarketingCampaignExecutor = (activity) => {
    const isScheduled = Boolean(activity?.Campaign?.ScheduleAt);
    if (isScheduled) {
        const user = activity?.audit?.modified?.user;
        if (user?.id && user?.name) {
            return { id: String(user.id), name: user.name };
        }
    }
    return {
        id: activity?.IdActivityOwner,
        name: activity?.ActivityOwner,
    };
};

/**
 * Resolves the Information-tab origin row: dynamic label + executor user.
 * Immediate → Sent by; scheduled → Scheduled by. Automation/Triggered by is out of scope.
 */
export const getMarketingCampaignOrigin = (activity) => {
    const isScheduled = Boolean(activity?.Campaign?.ScheduleAt);
    const executor = getMarketingCampaignExecutor(activity);

    return {
        labelKey: isScheduled
            ? MARKETING_CAMPAIGN_ORIGIN_LABEL.SCHEDULED_BY
            : MARKETING_CAMPAIGN_ORIGIN_LABEL.SENT_BY,
        id: executor.id,
        name: executor.name,
    };
};

/**
 * Global unique metrics for the Information tab. Always returns all rows (missing → 0).
 * Shape matches card metrics so the same icon + label formatting can be reused.
 */
export const getMarketingCampaignDetailGlobalMetrics = (globalMetrics) => [
    {
        key: 'opens-unique',
        icon: 'visibilityOn',
        value: toMetricValue(globalMetrics?.OpensUnique),
        labelKey: 'label_marketing_campaigns_activity_metric_opened',
    },
    {
        key: 'clicks-unique',
        icon: 'trackingLink',
        value: toMetricValue(globalMetrics?.ClicksUnique),
        labelKey: 'label_marketing_campaigns_activity_metric_clicks',
    },
    {
        key: 'unsubscribes',
        icon: 'close',
        value: toMetricValue(globalMetrics?.Unsubscribes),
        labelKey: 'label_marketing_campaigns_activity_metric_unsubscribed',
    },
];

/**
 * Individual recipient metrics for the Information tab (widget detail).
 * Opens/clicks always shown (missing → 0). Unsubscribed is boolean like the list card:
 * only rendered when true, label-only (no numeric value).
 */
export const getMarketingCampaignDetailIndividualMetrics = (individualMetrics) => {
    const metrics = [
        {
            key: 'opens',
            icon: 'visibilityOn',
            value: toMetricValue(individualMetrics?.Opens),
            labelKey: 'label_marketing_campaigns_activity_metric_opened',
        },
        {
            key: 'clicks',
            icon: 'trackingLink',
            value: toMetricValue(individualMetrics?.Clicks),
            labelKey: 'label_marketing_campaigns_activity_metric_clicks',
        },
    ];

    if (getBackendBoolean(individualMetrics?.Unsubscribed)) {
        metrics.push({
            key: 'unsubscribed',
            icon: 'close',
            labelKey: 'label_marketing_campaigns_activity_metric_unsubscribed',
            showValue: false,
        });
    }

    return metrics;
};

const INTERACTION_TYPE_LITERALS = {
    delivered: 'label_marketing_campaigns_metrics_delivered',
    open: 'label_marketing_campaigns_metrics_opened',
    click: 'label_marketing_campaigns_metrics_clicked',
    unsubscribe: 'label_marketing_campaigns_metrics_unsubscribed',
};

const INTERACTION_TYPE_ICONS = {
    delivered: 'checkCircle',
    open: 'visibilityOn',
    click: 'trackingLink',
    unsubscribe: 'close',
};

export const getMarketingCampaignInteractionType = (interaction) =>
    interaction?.Type || interaction?.type || '';

export const getMarketingCampaignInteractionTimestamp = (interaction) =>
    interaction?.Timestamp || interaction?.timestamp || '';

export const getMarketingCampaignInteractionLabel = (type) => {
    const literalKey = INTERACTION_TYPE_LITERALS[type];
    return literalKey ? getLiteral(literalKey) : type;
};

export const getMarketingCampaignInteractionIcon = (type) => INTERACTION_TYPE_ICONS[type] || 'info';

/**
 * Resolves destination URL for a click interaction (BE uses PascalCase `Url`).
 */
export const getMarketingCampaignInteractionUrl = (interaction) => {
    if (!interaction || typeof interaction !== 'object') return '';

    const url =
        interaction.Url ||
        interaction.url ||
        interaction.Link ||
        interaction.link ||
        interaction.href;

    return typeof url === 'string' && url ? url : '';
};

/**
 * Display-friendly URL (strip protocol) for click rows.
 */
export const getMarketingCampaignInteractionDisplayUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    return url.replace(/^https?:\/\//i, '');
};

const getInteractionSortValue = (interaction) => {
    const timestamp = getMarketingCampaignInteractionTimestamp(interaction);
    if (timestamp == null || timestamp === '') return Number.NEGATIVE_INFINITY;
    const parsed = Date.parse(timestamp);
    return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

/**
 * Returns IndividualMetrics.Interactions sorted newest-first when timestamps are comparable.
 */
export const getMarketingCampaignInteractions = (campaign) => {
    const interactions = campaign?.IndividualMetrics?.Interactions;
    if (!Array.isArray(interactions) || interactions.length === 0) return [];

    return [...interactions].sort(
        (a, b) => getInteractionSortValue(b) - getInteractionSortValue(a),
    );
};

const getMarketingCampaignMetricsSummary = (globalMetrics) => {
    if (!globalMetrics) return null;

    const parts = [
        {
            key: 'opens',
            label: getLiteral('label_marketing_campaigns_metrics_unique_opens'),
            value: globalMetrics.OpensUnique,
        },
        {
            key: 'clicks',
            label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
            value: globalMetrics.ClicksUnique,
        },
        {
            key: 'bounces',
            label: getLiteral('label_marketing_campaigns_metrics_bounces'),
            value: globalMetrics.Bounces,
        },
        {
            key: 'unsubscribes',
            label: getLiteral('label_marketing_campaigns_metrics_unsubscribed'),
            value: globalMetrics.Unsubscribes,
        },
    ].filter(({ value }) => value !== undefined && value !== null);

    if (!parts.length) return null;

    return parts.map(({ label, value }) => `${label}: ${value}`).join(' · ');
};

const getMarketingCampaignRecipientsSummary = (recipients) => {
    if (!recipients) return null;

    const parts = [
        recipients.Total !== undefined &&
            recipients.Total !== null && {
                label: getLiteral('label_total'),
                value: recipients.Total,
            },
        recipients.Contacts !== undefined &&
            recipients.Contacts !== null && {
                text: getLiteralPluralized(
                    recipients.Contacts,
                    'label_one_contact',
                    'label_contacts',
                ),
            },
        recipients.Accounts !== undefined &&
            recipients.Accounts !== null && {
                text: getLiteralPluralized(
                    recipients.Accounts,
                    'label_one_account',
                    'label_accounts',
                ),
            },
    ].filter(Boolean);

    if (!parts.length) return null;

    return parts.map(({ label, value, text }) => text || `${label}: ${value}`).join(' · ');
};

export const getMarketingCampaignCardMetrics = (globalMetrics) => {
    if (!globalMetrics) return [];

    return [
        {
            icon: 'visibilityOn',
            value: globalMetrics.OpensUnique,
            labelKey: 'label_marketing_campaigns_activity_metric_opened',
        },
        {
            icon: 'trackingLink',
            value: globalMetrics.ClicksUnique,
            labelKey: 'label_marketing_campaigns_activity_metric_clicks',
        },
        {
            icon: 'close',
            value: globalMetrics.Unsubscribes,
            labelKey: 'label_marketing_campaigns_activity_metric_unsubscribed',
        },
    ].filter(({ value }) => value !== undefined && value !== null);
};

export const getMarketingCampaignIndividualCardMetrics = (individualMetrics) => {
    if (!individualMetrics) return [];

    const metrics = [
        {
            icon: 'visibilityOn',
            value: individualMetrics.Opens,
            labelKey: 'label_marketing_campaigns_activity_metric_opened',
        },
        {
            icon: 'trackingLink',
            value: individualMetrics.Clicks,
            labelKey: 'label_marketing_campaigns_activity_metric_clicks',
        },
    ].filter(({ value }) => value !== undefined && value !== null);

    if (getBackendBoolean(individualMetrics.Unsubscribed)) {
        metrics.push({
            icon: 'close',
            labelKey: 'label_marketing_campaigns_activity_metric_unsubscribed',
            showValue: false,
        });
    }

    return metrics;
};

const METRIC_LABEL_FALLBACKS = {
    label_marketing_campaigns_activity_metric_opened:
        'label_marketing_campaigns_metrics_opened_plural',
    label_marketing_campaigns_activity_metric_clicks:
        'label_marketing_campaigns_metrics_clicked_plural',
    label_marketing_campaigns_activity_metric_unsubscribed:
        'label_marketing_campaigns_metrics_unsubscribed',
};

export const formatMarketingCampaignMetricLabel = (value, labelKey) => {
    const template = getLiteral(labelKey);

    if (template.includes('%@')) {
        return getLiteralWithParameters(labelKey, [value]);
    }

    if (template !== labelKey) {
        return `${value} ${template.toLowerCase()}`;
    }

    const fallbackKey = METRIC_LABEL_FALLBACKS[labelKey];
    if (fallbackKey) {
        return `${value} ${getLiteral(fallbackKey).toLowerCase()}`;
    }

    return `${value}`;
};

export const formatMarketingCampaignMetricLabelOnly = (labelKey) => {
    const template = getLiteral(labelKey);

    if (!template.includes('%@') && template !== labelKey) {
        return template;
    }

    const fallbackKey = METRIC_LABEL_FALLBACKS[labelKey];
    if (fallbackKey) {
        return getLiteral(fallbackKey);
    }

    return template.replace(/%@\s*/g, '').trim();
};

export const getMarketingCampaignRecipientsBreakdown = (recipients) => {
    if (!recipients) return null;

    const breakdown = [
        recipients.Contacts !== undefined &&
            recipients.Contacts !== null && {
                text: getLiteralPluralized(
                    recipients.Contacts,
                    'label_one_contact',
                    'label_number_contacts',
                ),
            },
        recipients.Accounts !== undefined &&
            recipients.Accounts !== null && {
                text: getLiteralPluralized(
                    recipients.Accounts,
                    'label_one_account',
                    'label_number_accounts',
                ),
            },
        recipients.Lists !== undefined &&
            recipients.Lists !== null && {
                text: getLiteralPluralized(
                    recipients.Lists,
                    'label_one_list',
                    'label_number_lists',
                ),
            },
    ].filter(Boolean);

    return {
        total: recipients.Total,
        breakdown,
    };
};
